<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4 pb-3 border-bottom border-subtle d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          <i class="mdi mdi-hand-heart text-info mr-2"></i>{{ $t('quests.compensationsTitle') }}
        </h3>
        <p class="text-muted small mb-0">{{ $t('quests.compensationsSubtitle') }}</p>
      </div>
      <div class="mt-3 mt-md-0">
        <router-link
          :to="{ name: 'CreateCompensation' }"
          class="btn btn-outline-info btn-sm px-4 font-weight-bold d-inline-flex align-items-center"
        >
          <i class="mdi mdi-plus-circle mr-1"></i>
          {{ $t('quests.createCompensation') }}
        </router-link>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <!-- Search & Filter Bar -->
          <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-2">
            <div class="search-box" style="max-width: 360px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('quests.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-bold">{{ filteredCompensation.length }} {{ $currentLocale === 'id' ? 'Tugas' : 'Tasks' }}</span>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 45px;">No</th>
                  <th>{{ $t('quests.compensationTask') }}</th>
                  <th style="width: 380px;">{{ $t('quests.description') }}</th>
                  <th class="text-right" style="width: 110px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedCompensation" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <router-link
                      :to="{ name: 'DetailCompensation', params: { id: String(data.id) } }"
                      class="text-white font-weight-bold text-decoration-none"
                    >
                      <i class="mdi mdi-hand-heart text-info mr-1"></i>
                      {{ data.name }}
                    </router-link>
                  </td>
                  <td style="max-width: 400px;">
                    <span class="text-muted small text-truncate d-block">{{ data.description || '-' }}</span>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailCompensation', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditCompensation', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button
                        type="button"
                        class="btn btn-action-icon btn-outline-danger"
                        @click="handleDelete(data.id)"
                        :title="$t('common.delete')"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedCompensation.length === 0">
                  <td colspan="4" class="text-center py-5">
                    <EmptyState
                      icon="mdi-hand-heart-outline"
                      :title="$t('quests.emptyCompTitle')"
                      :description="$t('quests.emptyCompDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredCompensation.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredCompensation.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCompensation, deleteCompensation } from "../../services/compensationService";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import Swal from "sweetalert2";

export default {
  name: "CompensationList",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: "",
      compensation: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
    };
  },
  computed: {
    filteredCompensation() {
      if (!this.search) return this.compensation;
      const q = this.search.toLowerCase();
      return this.compensation.filter((item) => {
        const nameMatch = (item.name || "").toLowerCase().includes(q);
        const descMatch = (item.description || "").toLowerCase().includes(q);
        return nameMatch || descMatch;
      });
    },
    paginatedCompensation() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCompensation.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.fetchCompensations();
  },
  methods: {
    async fetchCompensations() {
      this.loading = true;
      try {
        const response = await getCompensation();
        this.compensation = response.data || [];
      } catch (e) {
        // handled
      } finally {
        this.loading = false;
      }
    },
    async handleDelete(id) {
      const result = await Swal.fire({
        title: this.$t("common.confirm") || (this.$currentLocale === 'id' ? 'Konfirmasi' : 'Confirm'),
        text: this.$currentLocale === 'id' ? 'Hapus tugas kompensasi / penebusan ini?' : 'Delete this recovery / compensation task?',
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: this.$t("common.delete") || (this.$currentLocale === 'id' ? 'Hapus' : 'Delete'),
        cancelButtonText: this.$t("common.cancel") || (this.$currentLocale === 'id' ? 'Batal' : 'Cancel'),
        background: "#12141c",
        color: "#fff",
      });
      if (!result.isConfirmed) return;

      try {
        await deleteCompensation(id);
        Swal.fire({
          icon: "success",
          title: this.$t("common.success") || (this.$currentLocale === 'id' ? 'Berhasil' : 'Success'),
          text: this.$currentLocale === 'id' ? 'Tugas kompensasi berhasil dihapus.' : 'Recovery task deleted successfully.',
          timer: 1500,
          showConfirmButton: false,
          background: "#12141c",
          color: "#fff",
        });
        await this.fetchCompensations();
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("common.error") || (this.$currentLocale === 'id' ? 'Terjadi Kesalahan' : 'Error'),
          text: this.$currentLocale === 'id' ? 'Gagal menghapus tugas kompensasi.' : 'Failed to delete compensation task.',
          background: "#12141c",
          color: "#fff",
        });
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>
